// ─────────────────────────────────────────────────────────────────────────────
// Criação de tickets em massa.
//
// Serve para o caso em que um mesmo problema atinge vários clientes: o operador
// escreve UMA vez e o Orion abre um ticket para cada um, com a mesma
// classificação e a interação podendo ser ajustada cliente a cliente.
//
// Portado do Orion 1.4.1 (renderer.js, bloco "Em Massa").
// ─────────────────────────────────────────────────────────────────────────────

import { tabelaDoSchema } from './supabase';
import { obterMapaPendentes } from './mysuiteApi';
import { normalizarParaComparar, encontrarMelhorMatch } from './texto';
import { WEBHOOKS, MAPA_CATEGORIAS } from '../config/ambiente';

/** Status final por tipo de atendimento — mesma tabela do envio individual. */
const STATUS_POR_TIPO = {
  'Conclusão': 3,
  'Aguardando interação de cliente': 2,
  'Setor de sistemas': 18,
  'Suporte avançado': 91,
  'Comercial': 82,
  'Na fila para retorno': 5,
};

/** Operador de destino por tipo. 0 = fica com quem criou. */
const OPERADOR_POR_TIPO = {
  'Setor de sistemas': 108,
  'Suporte avançado': 106,   // Análise Suporte
};

export { MAPA_CATEGORIAS };

/**
 * Lista de clientes para o lote.
 *
 * Junta duas fontes e é essa junção que dá valor à tela:
 *   · pulse.empresas_config → o cadastro, com CNPJ e razão social;
 *   · a fila de pendentes do MySuite → quem JÁ tem ticket aberto, e de onde
 *     saem os códigos que o ticket novo precisa (usuário do cliente, setor,
 *     categoria, prioridade).
 *
 * Quem tem pendente sobe para o topo: é o cliente sobre o qual já se sabe algo.
 */
export async function carregarClientes(ticketInfoAtual = null) {
  let clientes = [];

  try {
    const { data, error } = await tabelaDoSchema('pulse', 'empresas_config')
      .select('cliente, razao_social, nome_empresa, cnpj')
      .order('razao_social', { ascending: true });

    if (!error && data) {
      clientes = data.map(c => ({
        codigo: String(c.cliente),
        nome: c.nome_empresa || c.razao_social || `Cliente ${c.cliente}`,
        cnpj: c.cnpj || '',
        fonte: 'cadastro',
        codigoCliente: c.cliente,
        codigoUsuarioCliente: null,
        codigoSetor: 3,
        codigoCategoria: 1,
        prioridade: 1,
        cnpjCliente: c.cnpj || '',
        temPendente: false,
      }));
    }
  } catch (err) {
    console.warn('[Orion] Clientes (pulse):', err.message);
  }

  // Enriquece com o que a fila de pendentes já sabe.
  const pendentes = Object.values(obterMapaPendentes() || {});
  const porCnpj = {};
  pendentes.forEach(p => { if (p.cnpjCliente) porCnpj[p.cnpjCliente] = p; });

  clientes.forEach(c => {
    const pend = porCnpj[c.cnpj];
    if (!pend) return;
    c.codigoUsuarioCliente = pend.codigoUsuarioCliente ?? null;
    c.codigoSetor = pend.codigoSetorTicket ?? 3;
    c.codigoCategoria = pend.codigoCategoria ?? 1;
    c.prioridade = pend.prioridade ?? 1;
    c.temPendente = true;
  });

  // O ticket aberto agora sabe quem abriu (codigoUsuarioCliente). Se algum
  // cliente do lote tem o MESMO CNPJ e ainda está sem código, aproveita.
  // NÃO se aplica aos demais: o código é daquele solicitante, não do cliente.
  if (ticketInfoAtual?.cnpjCliente) {
    const cnpjTicket = ticketInfoAtual.cnpjCliente;
    const codUsuario = parseInt(ticketInfoAtual.codigoUsuarioCliente, 10) || null;
    if (codUsuario) {
      clientes.forEach(c => {
        if (!c.codigoUsuarioCliente && (c.cnpj === cnpjTicket || c.cnpjCliente === cnpjTicket)) {
          c.codigoUsuarioCliente = codUsuario;
        }
      });
    }
  }

  // Cliente que tem pendente mas não está no cadastro entra assim mesmo.
  const jaListados = new Set(clientes.map(c => c.cnpj).filter(Boolean));
  pendentes.forEach(p => {
    if (!p.cnpjCliente || jaListados.has(p.cnpjCliente)) return;
    clientes.push({
      codigo: `pend_${p.codigoCliente}`,
      nome: `Cliente ${p.codigoCliente}`,
      cnpj: p.cnpjCliente,
      fonte: 'pendente',
      codigoCliente: p.codigoCliente,
      codigoUsuarioCliente: p.codigoUsuarioCliente ?? null,
      codigoSetor: p.codigoSetorTicket ?? 3,
      codigoCategoria: p.codigoCategoria ?? 1,
      prioridade: p.prioridade ?? 1,
      cnpjCliente: p.cnpjCliente,
      temPendente: true,
    });
  });

  clientes.sort((a, b) => {
    if (a.temPendente && !b.temPendente) return -1;
    if (!a.temPendente && b.temPendente) return 1;
    return normalizarParaComparar(a.nome).localeCompare(normalizarParaComparar(b.nome));
  });

  return clientes;
}

/** Filtra a lista por nome ou CNPJ, sem diferenciar acento nem caixa. */
export function filtrarClientes(clientes, termo) {
  const t = normalizarParaComparar(termo || '');
  if (!t) return clientes;
  return clientes.filter(c =>
    normalizarParaComparar(c.nome).includes(t) || String(c.cnpj || '').includes(termo.trim())
  );
}

/**
 * Resolve os campos que valem igual para todo o lote.
 * A classificação é texto: casa pelo nome exato e, se não achar, pela
 * comparação tolerante.
 */
export function resolverCampos({ ocorrencia, criticidade, categoria, classificacao }, listas) {
  const acharNome = (lista, codigo) => lista.find(i => String(i.codigo) === String(codigo))?.nome || '';

  const classObj = classificacao
    ? (listas.classificacoes.find(c => c.nome.toLowerCase() === classificacao.toLowerCase())
      || encontrarMelhorMatch(classificacao, listas.classificacoes.map(c => ({ valor: String(c.codigo), texto: c.nome }))))
    : null;

  const nomeCategoria = Object.entries(MAPA_CATEGORIAS)
    .find(([, cod]) => String(cod) === String(categoria))?.[0] || '';

  return {
    codigoOcorrencia: parseInt(ocorrencia, 10) || 0,
    ocorrencia: acharNome(listas.ocorrencias, ocorrencia),
    codigoCriticidade: parseInt(criticidade, 10) || 0,
    criticidade: acharNome(listas.criticidades, criticidade),
    codigoCategoria: parseInt(categoria, 10) || 0,
    categoria: nomeCategoria,
    codigoClassificacao: classObj ? (parseInt(classObj.codigo ?? classObj.valor, 10) || 0) : 0,
    classificacao: classificacao || '',
  };
}

/**
 * Cria os tickets, um a um, avisando o progresso.
 *
 * Sequencial de propósito: são chamadas ao n8n que criam ticket de verdade no
 * MySuite. Disparar tudo em paralelo tira a ordem dos números e dificulta saber
 * onde parou se algo falhar no meio.
 *
 * Devolve a lista de resultados — um por cliente, com ok/erro e o número gerado.
 */
export async function criarEmMassa({
  clientes, titulo, interacaoBase, interacoesPorCliente = {},
  tipoAtendimento, campos, operador, codigoOperadorPadrao, prioridadeContexto,
}, aoProgredir) {
  const resultados = [];
  const codigoStatus = STATUS_POR_TIPO[tipoAtendimento] || 36;
  const codigoOperador = OPERADOR_POR_TIPO[tipoAtendimento] ?? (codigoOperadorPadrao || 0);

  for (let i = 0; i < clientes.length; i++) {
    const cliente = clientes[i];
    if (aoProgredir) aoProgredir({ indice: i, total: clientes.length, cliente });

    const personalizada = interacoesPorCliente[cliente.codigo];
    const interacao = (personalizada && personalizada.trim()) ? personalizada.trim() : interacaoBase;

    const payload = {
      codigoCategoria: campos.codigoCategoria || cliente.codigoCategoria || 1,
      categoria: campos.categoria,
      codigoStatus,
      codigoCriticidade: campos.codigoCriticidade,
      criticidade: campos.criticidade,
      codigoOcorrencia: campos.codigoOcorrencia,
      ocorrencia: campos.ocorrencia,
      titulo,
      interacao,
      codigoUsuarioCliente: cliente.codigoUsuarioCliente ?? 0,
      codigoSetor: cliente.codigoSetor || 3,
      codigoOperador,
      codigoClassificacao: campos.codigoClassificacao,
      classificacao: campos.classificacao,
      cnpjCliente: cliente.cnpjCliente || cliente.cnpj || '',
      prioridade: prioridadeContexto || cliente.prioridade || 1,
      dataPrevisao: '0000-00-00',
      horaPrevisao: '00:00:00',
      tipoAtendimento,
      ticketOrigem: null,
      operadorNome: operador?.nome || '',
      operadorEmail: operador?.email || '',
      clienteNome: cliente.nome,
    };

    try {
      const res = await fetch(WEBHOOKS.inserirTicket, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // O n8n devolve o número do ticket em formatos diferentes conforme o
      // fluxo. Procurar a primeira sequência de 5 a 8 dígitos na resposta é o
      // que funciona para todos eles — é assim desde a 1.x.
      let numero = null;
      try {
        let data = await res.json();
        if (Array.isArray(data) && data.length > 0) data = data[0];
        const achado = JSON.stringify(data).match(/(\d{5,8})/);
        if (achado) numero = achado[1];
      } catch (e) { /* resposta sem JSON: o ticket foi criado, só não sabemos o número */ }

      resultados.push({ cliente, ok: true, numero });
    } catch (err) {
      resultados.push({ cliente, ok: false, erro: err.message });
    }
  }

  return resultados;
}
